import { vec3 } from 'gl-matrix';

/**
 * Sorts DICOM instances based on their ImagePositionPatient along the scan axis.
 *
 * ## Problem Solved
 * Previously, this function crashed with `TypeError: Cannot read properties of undefined (reading '0')`
 * when some instances lacked `ImagePositionPatient` metadata. This could happen with:
 * - Scout/localizer images mixed with regular slices
 * - Incomplete DICOM metadata
 * - Secondary capture images
 *
 * ## Fix Applied
 * 1. Filter instances into two groups BEFORE reference selection:
 *    - `instancesWithPosition`: instances with valid `ImagePositionPatient` array
 *    - `instancesWithoutPosition`: instances lacking position data
 * 2. Select reference from filtered instances (ensures reference is always valid)
 * 3. Sort only instances with position data
 * 4. Append instances without position at the end
 *
 * @param instances - Array of DICOM instance metadata objects
 * @returns Sorted instances array with position-less instances appended at the end
 */
export default function sortInstances(instances: Array<any>) {
  // Filter instances that have valid ImagePositionPatient for sorting
  const instancesWithPosition = instances.filter(
    instance => instance.ImagePositionPatient && Array.isArray(instance.ImagePositionPatient)
  );
  const instancesWithoutPosition = instances.filter(
    instance => !instance.ImagePositionPatient || !Array.isArray(instance.ImagePositionPatient)
  );

  // Debug logging to track which path is being used
  if (instancesWithoutPosition.length > 0) {
    console.warn(
      `[sortInstancesByPosition] ABNORMAL CASE: ${instancesWithoutPosition.length}/${instances.length} instances missing ImagePositionPatient. Using defensive sorting.`
    );
  } else {
    console.warn(
      `[sortInstancesByPosition] NORMAL CASE: All ${instances.length} instances have ImagePositionPatient. Using standard sorting.`
    );
  }

  // If no instances have position info, return original array
  if (instancesWithPosition.length === 0) {
    console.warn('[sortInstancesByPosition] No instances have ImagePositionPatient. Returning unsorted.');
    return instances;
  }

  // If only one instance has position info, put it first and append the rest
  if (instancesWithPosition.length === 1) {
    console.warn('[sortInstancesByPosition] Only 1 instance has ImagePositionPatient. Skipping sort.');
    return [...instancesWithPosition, ...instancesWithoutPosition];
  }

  // Select reference from filtered instances (middle one)
  const { ImagePositionPatient: referenceImagePositionPatient, ImageOrientationPatient } =
    instancesWithPosition[Math.floor(instancesWithPosition.length / 2)];

  if (!referenceImagePositionPatient || !ImageOrientationPatient) {
    return instances;
  }

  const rowCosineVec = vec3.fromValues(
    ImageOrientationPatient[0],
    ImageOrientationPatient[1],
    ImageOrientationPatient[2]
  );
  const colCosineVec = vec3.fromValues(
    ImageOrientationPatient[3],
    ImageOrientationPatient[4],
    ImageOrientationPatient[5]
  );

  const scanAxisNormal = vec3.cross(vec3.create(), rowCosineVec, colCosineVec);

  const refIppVec = vec3.set(
    vec3.create(),
    referenceImagePositionPatient[0],
    referenceImagePositionPatient[1],
    referenceImagePositionPatient[2]
  );

  const distanceInstancePairs = instancesWithPosition.map(instance => {
    const imagePositionPatient = instance.ImagePositionPatient;

    const positionVector = vec3.create();

    vec3.sub(positionVector, referenceImagePositionPatient, imagePositionPatient);

    const distance = vec3.dot(positionVector, scanAxisNormal);

    return {
      distance,
      instance,
    };
  });

  distanceInstancePairs.sort((a, b) => b.distance - a.distance);

  const sortedInstances = distanceInstancePairs.map(a => a.instance);

  // Append instances without position info at the end to preserve all instances
  return [...sortedInstances, ...instancesWithoutPosition];
}
